import { useEffect, useState, type FormEvent } from 'react';
import '../styles/RecipientInfoModal.css';

type RecipientInfo = {
    name: string;
    email: string;
    phone: string;
};

type RecipientInfoModalProps = {
    open: boolean;
    submitting?: boolean;
    error?: string | null;
    onSubmit: (info: RecipientInfo) => Promise<void> | void;
};

const RecipientInfoModal = ({ open, submitting = false, error, onSubmit }: RecipientInfoModalProps) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [localError, setLocalError] = useState<string | null>(null);
    const formatPhone = (value: string): string => {
        const digits = value.replace(/\D/g, '').slice(0, 10);
        if (digits.length <= 3) return digits;
        if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    };

    useEffect(() => {
        if (!open) {
            setLocalError(null);
        }
    }, [open]);

    const validate = (): string | null => {
        const trimmedName = name.trim();
        if (!trimmedName || trimmedName.length < 3) {
            return 'Please enter your name';
        }
        const trimmedEmail = email.trim();
        if (!trimmedEmail || trimmedEmail.length < 7 || trimmedEmail.indexOf('@') === -1 || trimmedEmail.indexOf('.') === -1) {
            return 'Please enter your email address';
        }
        return null;
    };

    const handleSubmit = async (event: FormEvent) => {
        event.preventDefault();
        const validationError = validate();
        if (validationError) {
            setLocalError(validationError);
            return;
        }
        setLocalError(null);
        await onSubmit({
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim()
        });
    };

    if (!open) {
        return null;
    }

    return (
        <div className="recipient-modal" role="dialog" aria-modal="true">
            <div className="recipient-modal__backdrop" />
            <div className="recipient-modal__dialog">
                <h1>Please enter your information to view your customized presentation!</h1>
                <p>We value your privacy and your information will not be shared.</p>

                <form onSubmit={handleSubmit} className="recipient-modal__form">
                    <label>
                        <span>Your Name</span>
                        <input
                            id="recipientName"
                            type="text"
                            value={name}
                            onChange={(event) => setName(event.target.value)}
                            autoComplete="name"
                            required
                        />
                    </label>

                    <label>
                        <span>Email Address</span>
                        <input
                            id="recipientEmail"
                            type="email"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            autoComplete="email"
                            required
                        />
                    </label>

                    <label>
                        <span>Phone Number <em>(optional)</em></span>
                        <input
                            id="recipientPhone"
                            type="tel"
                            value={formatPhone(phone)}
                            inputMode="numeric"
                            onChange={(event) => setPhone(event.target.value.replace(/\D/g, ''))}
                            autoComplete="tel"
                        />
                    </label>

                    {(localError || error) && (
                        <div className="recipient-modal__error">
                            {localError || error}
                        </div>
                    )}

                    <button type="submit" disabled={submitting}>
                        {submitting ? 'Starting…' : 'Begin'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default RecipientInfoModal;
